<script lang="ts" setup>
import type { ContactUsConfig } from '../configs/types'

const props = defineProps<{
  config: ContactUsConfig
  des: string
  email: string
  loading: boolean
  handleConfirm: () => void
}>()

const emit = defineEmits<{
  (event: 'update:des', value: string): void
  (event: 'update:email', value: string): void
}>()

function updateField(field: 'des' | 'email', event: Event) {
  emit(`update:${field}`, (event.target as HTMLInputElement | HTMLTextAreaElement).value)
}

const textTertiary = props.config.colors.textTertiary
const textPlaceholder = props.config.colors.textPlaceholder
const accentRed = props.config.colors.accentRed
const whiteColor = props.config.colors.whiteColor
</script>

<template>
  <div class="contact-us-page">
    <div class="h-44px">
      <BasicNavbar opacity class="color-n-text-2">
        <div class="novelyn-nav-title">
          Contact Us
        </div>
      </BasicNavbar>
    </div>
    <div class="form-container">
      <div class="form-item">
        <div class="form-title">
          <span class="required">*</span>{{ config.texts.feedbackTitle }}
        </div>
        <div class="textarea-wrapper">
          <textarea
            :value="des"
            class="form-textarea"
            :placeholder="config.texts.feedbackPlaceholder"
            maxlength="100"
            @input="updateField('des', $event)"
          />
          <div class="char-count">
            {{ des.length }}/100
          </div>
        </div>
      </div>

      <div class="form-item">
        <div class="form-title">
          <span class="required">*</span>{{ config.texts.emailTitle }}
        </div>
        <input
          :value="email"
          type="text"
          class="form-input"
          :placeholder="config.texts.emailPlaceholder"
          @input="updateField('email', $event)"
        >
      </div>

      <button
        :disabled="des === '' || email === '' || loading"
        class="submit-btn"
        :class="{ 'submit-btn-loading': loading }"
        @click="handleConfirm"
      >
        {{ loading ? config.texts.submittingButton : config.texts.submitButton }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-us-page {
  min-height: 100vh;
  background: var(--novelyn-bg);
}

.novelyn-nav-title {
  position: absolute;
  inset: 0 58px;
  display: grid;
  place-items: center;
  color: var(--novelyn-ink);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 17px;
  font-weight: 700;
  pointer-events: none;
}

.form-container {
  padding: 2px 20px 36px;
}

.form-item {
  margin-bottom: 18px;
}

.form-title {
  padding: 12px 4px 10px;
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: 16px;
  color: var(--novelyn-ink);

  .required {
    color: v-bind(accentRed);
  }
}

.textarea-wrapper {
  position: relative;
}

.form-textarea {
  width: 100%;
  height: 148px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--novelyn-line);
  padding: 14px 16px;
  font-size: 14px;
  color: var(--novelyn-ink);
  box-shadow: var(--novelyn-shadow-sm);
  resize: none;
  box-sizing: border-box;
  font-family: inherit;

  &::placeholder {
    color: v-bind(textPlaceholder);
  }

  &:focus {
    outline: none;
  }
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: v-bind(textTertiary);
}

.form-input {
  width: 100%;
  height: 46px;
  background: #fff;
  border-radius: 14px !important;
  border: 1px solid var(--novelyn-line) !important;
  padding: 0 16px;
  font-size: 14px;
  color: var(--novelyn-ink);
  box-shadow: var(--novelyn-shadow-sm);
  box-sizing: border-box;

  &::placeholder {
    color: v-bind(textPlaceholder);
  }

  &:focus {
    outline: none;
  }
}

.submit-btn {
  display: block;
  margin: 30px auto 0;
  width: 100%;
  height: 46px;
  border-radius: 60px;
  background: var(--novelyn-gradient);
  color: v-bind(whiteColor);
  font-size: 16px;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(224, 90, 126, .28);
  transition: opacity 0.3s;

  &:disabled {
    color: #B9A5AF !important;
    background: #EBDDE3 !important;
    box-shadow: none !important;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    opacity: 0.8;
  }

  &-loading {
    opacity: 0.8;
  }
}
</style>
